import { stringify } from 'query-string';
import { GET_MANY, GET_MANY_REFERENCE, GET_LIST, GET_ONE, CREATE, UPDATE, DELETE, DELETE_MANY, UPDATE_MANY, fetchUtils } from 'react-admin';
import debug from 'debug';

/**
 * Maps react-admin queries to a simple REST API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_LIST     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?filter={ids:[123,456,789]}
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts/123
 * DELETE       => DELETE http://my.api.url/posts/123
 */
export default (clientOptions = {}) => {
  /**
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The REST request params, depending on the type
   * @returns {Object} { url, options } The HTTP request parameters
   */
  const convertRESTRequestToHTTP = (type, resource, params = {}) => {
    let { apiUrl, storageKey, languageKey } = { ...{ storageKey: 'user' }, ...clientOptions };

    if (apiUrl.endsWith('/')) {
      apiUrl = apiUrl.substr(0, apiUrl.length - 1);
    }

    let url = '';
    let options = {};
    const userStorage = localStorage.getItem(storageKey);
    if (userStorage) {
      const user = JSON.parse(userStorage);
      if (user && user.token) {
        options.user = {
          authenticated: true,
          token: 'Bearer ' + user.token,
        };
      }
    }

    const currentLanguage = localStorage.getItem(languageKey);
    options.headers = new Headers({
      Accept: 'application/json',
      'x-language': currentLanguage || 'mk',
    });

    switch (type) {
      case GET_LIST: {
        let restParams = { ...params };
        delete restParams.pagination;
        delete restParams.sort;
        delete restParams.filter;

        const query = { ...restParams };

        if (params.sort) {
          query.sort = JSON.stringify([params.sort.field, params.sort.order]);
        }

        if (params.filter) {
          query.filter = JSON.stringify(params.filter);
        }

        query.hideDeleted = false;

        if (params.pagination) {
          query.offset = (params.pagination.page - 1) * params.pagination.perPage;
          query.size = params.pagination.perPage;
        }

        url = `${apiUrl}/${resource}?${stringify(query)}`;
        debug(url);
        break;
      }
      case GET_ONE:
        url = `${apiUrl}/${resource}/${encodeURIComponent(params.id)}`;
        break;
      case GET_MANY: {
        const query = {
          filter: JSON.stringify({ id: params.ids }),
        };
        url = `${apiUrl}/${resource}?${stringify(query)}`;
        break;
      }
      case GET_MANY_REFERENCE: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
          sort: JSON.stringify([field, order]),
          offset: (page - 1) * perPage,
          size: perPage,
          filter: JSON.stringify({
            ...params.filter,
            [params.target]: params.id,
          }),
        };
        url = `${apiUrl}/${resource}?${stringify(query)}`;
        break;
      }
      case UPDATE:
        url = `${apiUrl}/${resource}/${encodeURIComponent(params.id)}`;
        options.method = 'PATCH';
        
        options.body = JSON.stringify(params.data);
        break;
      case UPDATE_MANY:
        const update_query = {
          filter: JSON.stringify({ id: params.ids }),
        };
        url = `${apiUrl}/${resource}?${stringify(update_query)}`;
        options.method = 'PATCH';
        options.body = JSON.stringify(params.data);
        break;
      case DELETE_MANY:
        const delete_query = {
          filter: JSON.stringify({ id: params.ids }),
        };
        url = `${apiUrl}/${resource}?${stringify(delete_query)}`;
        options.method = 'DELETE';
        break;
      case 'CRAWL':
      case CREATE:
        url = `${apiUrl}/${resource}`;
        options.method = 'POST';
        options.body = JSON.stringify(params.data);
        break;
      case DELETE:
        url = `${apiUrl}/${resource}/${encodeURIComponent(params.id)}`;
        options.method = 'DELETE';
        break;
      default:
        throw new Error(`Unsupported fetch action type ${type}`);
    }
    return { url, options };
  };

  /**
   * @param {Object} response HTTP response from fetch()
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The REST request params, depending on the type
   * @returns {Object} REST response
   */
  const convertHTTPResponseToREST = (response, type, resource, params) => {
    const { json } = response;
    if (!json) {
      return { data: [], total: 0 };
    }
    switch (type) {
      case 'CRAWL':
      case GET_LIST:
      case GET_MANY:
      case GET_MANY_REFERENCE:
        if (json.items) {
          return {
            data: json.items,
            total: json.total || json.items.length,
          };
        }
        return { data: json };
      case CREATE:
        return { data: { ...params.data, id: json.id } };
      default:
        return { data: json };
    }
  };

  /**
   * @param {string} type Request type, e.g GET_LIST
   * @param {string} resource Resource name, e.g. "posts"
   * @param {Object} payload Request parameters. Depends on the request type
   * @returns {Promise} the Promise for a REST response
   */
  return (type, resource, params) => {
    let { client } = { ...{ client: fetchUtils.fetchJson } };
    const { url, options } = convertRESTRequestToHTTP(type, resource, params);
    return client(url, options).then(response => convertHTTPResponseToREST(response, type, resource, params));
  };
};



// WEBPACK FOOTER //
// ./src/services/restClient.js